/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * The action for Table component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Misha Kokoulin
 * @author Maxim Fedorov
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { createAction } from '@modules/app/createAction';
import type { ILogoutStartSuccessful } from '@pages/PageAuth/types/authTypes';

import type { TLabelEditorUpdatePayload } from '../components/footerModal/LabelEditor/labelEditorTypes';
import {
    ADD_TABLE_SUB_ROW_ID,
    CHANGE_TABLE_MODAL_VALUE,
    COLLAPSE_TABLE_SUB_ROWS,
    DROP_TABLE_FILTER_AND_SORT,
    FAST_ACTION_SWITCH_CHECKBOX,
    GET_BASKET_COUNT_FAILED,
    GET_BASKET_COUNT_SUCCESSFUL,
    LOAD_MOBILE_CASH,
    LOAD_TABLE_FIELDS_ERROR,
    LOAD_TABLE_FIELDS_SUCCESSFUL,
    LOAD_TABLE_LIST,
    LOAD_TABLE_LIST_ERROR,
    LOAD_TABLE_LIST_FULLY,
    LOAD_TABLE_LIST_SUCCESSFUL,
    LOAD_TABLE_META,
    LOAD_TABLE_META_ERROR,
    LOAD_TABLE_META_PARAMETERS_SUCCESSFUL,
    LOAD_TABLE_META_SUCCESSFUL,
    LOAD_TABLE_SUB_ROW,
    LOAD_TABLE_SUB_ROW_ERROR,
    MOBILE_CASH_FILE_LOADING,
    MOBILE_CASH_FILE_SUCCESSFULL,
    OPEN_BASKET,
    OPEN_CREATE_INQUIRY,
    OPEN_FOOTER_WIZARD_EXPENSES_DETAIL,
    OPEN_LABEL_EDITOR,
    OPEN_MOBILE_CASH,
    OPEN_MOBILE_CHANGE_SERVICE,
    OPEN_MOBILE_CHANGE_TARIFF,
    OPEN_MOBILE_CHANGROLE,
    RESET_BASKET,
    RESET_BASKET_COUNT,
    RESET_BASKET_SUCCESS,
    SEND_FIELD_SUGGEST,
    SEND_FIELD_SUGGEST_FAILED,
    SEND_FIELD_SUGGEST_SUCCESSFUL,
    SEND_TABLE_FIELDS,
    SEND_TABLE_FIELDS_SIZE,
    SEND_TABLE_FILTER_AND_SORT_ERROR,
    SEND_TABLE_FILTER_AND_SORT_SUCCESSFUL,
    SEND_TABLE_SEARCH,
    SEND_TABLE_SEARCH_ERROR,
    SEND_TABLE_SEARCH_SUCCESSFUL,
    SET_BASKET_CONTEXT,
    SET_BASKET_PARAMS,
    SET_BASKET_VALID,
    SET_IS_FAST_ACTION_PAGE,
    SET_IS_FAST_BASKET_PAGE,
    SET_RANGE_MONTH_FILTER,
    SET_RANGE_MONTH_FILTER_SUCCESS,
    SET_TABLE_CURRENT_NAME,
    SET_TABLE_FILTER,
    SET_TABLE_FILTER_PRESET,
    SET_TABLE_IS_LOADING,
    SET_TABLE_MAIN_CHECKBOX,
    SET_TABLE_SORT,
    SET_TABLE_SUB_ROW_DATA,
    SET_TABLE_SUB_ROW_MESSAGE,
    START_TICK,
    STOP_TICK,
    SWITCH_CHECKBOX,
    SWITCH_CHECKBOX_LOADING,
    SWITCH_CHECKBOX_SUCCESSFUL,
    TABLE_BUTTONS_SET_MODE,
    TABLE_MODAL_OPEN_REMOVE,
    TABLE_REMOVE_ROW_ACTION,
    TABLE_REMOVE_ROW_FAILURE,
    TABLE_REMOVE_ROW_SUCCESS,
    TABLE_RESET,
    TABLE_ROW_DELETE_ACTION,
    TABLE_ROW_RESTORE_ACTION,
    TABLE_ROW_RESTORE_ALL_ACTION,
    UPDATE_ROWS_START,
    UPDATE_ROWS_START_FAILURE,
    UPDATE_ROWS_START_SUCCESSFUL,
} from '../constants/actionConstants';
import type { ITableBasketParams, ITableReducer } from '../reducers/tableReducer';
import type {
    IAddTableSubRowIdPayload,
    IChangeTableModalValuePayload,
    IDropTableFilterAndSortPayload,
    ILoadMobileCashPayload,
    ILoadTableFieldsErrorPayload,
    ILoadTableFieldsSuccessfulPayload,
    ILoadTableListErrorPayload,
    ILoadTableListPayload,
    ILoadTableListSuccessfulPayload,
    ILoadTableMetaErrorPayload,
    ILoadTableMetaPayload,
    ILoadTableMetaSuccessfulPayload,
    ILoadTableSubRowErrorPayload,
    ILoadTableSubRowPayload,
    IOpenBasketActionPayload,
    IOpenCreateInquiryPayload,
    IOpenMobileCashPayload,
    IResetBasketActionPayload,
    IResetBasketCountActionPayload,
    ISendFieldSuggestPayload,
    ISendTableFieldsPayload,
    ISendTableFieldsSizePayload,
    ISendTableFilterAndSortErrorPayload,
    ISendTableSearchErrorPayload,
    ISendTableSearchPayload,
    ISendTableSearchSuccessfulPayload,
    ISetRangeMonthFilterPayload,
    ISetTableFilterPayload,
    ISetTableFilterPresetPayload,
    ISetTableIsLoadingPayload,
    ISetTableMainCheckboxPayload,
    ISetTableSortPayload,
    ISetTableSubRowDataPayload,
    ISetTableSubRowMessagePayload,
    ISwitchCheckboxPayload,
    ITableModalOpenRemoveActionPayload,
    ITableRemoveRowActionPayload,
    ITableRemoveRowFailurePayload,
    ITableRemoveRowSuccessPayload,
    ITableResetActionPayload,
    ITableRowActionPayload,
    ITableTableNamePayload,
    IUpdateRowsStartPayload,
    TableNameType,
    TAddTableSubRowIdAction,
    TChangeTableModalValueAction,
    TCollapseTableSubRows,
    TDropTableFilterAndSortAction,
    TFastActionSwitchCheckboxAction,
    TGetBasketCountAction,
    TGetBasketCountFailedAction,
    TGetBasketCountSuccessfulAction,
    TLoadMobileCashAction,
    TLoadTableFieldsAction,
    TLoadTableFieldsErrorAction,
    TLoadTableFieldsSuccessfulAction,
    TLoadTableFilterAndSortAction,
    TLoadTableFilterAndSortErrorAction,
    TLoadTableFilterAndSortSuccessfulAction,
    TLoadTableListAction,
    TLoadTableListErrorAction,
    TLoadTableListFullyAction,
    TLoadTableListSuccessfulAction,
    TLoadTableMetaAction,
    TLoadTableMetaErrorAction,
    TLoadTableMetaParametersSuccessfulAction,
    TLoadTableMetaSuccessfulAction,
    TLoadTableSubRowAction,
    TLoadTableSubRowErrorAction,
    TMobileCashFileLoading,
    TMobileCashFileLoadingSuccesful,
    TOpenBasketAction,
    TOpenCreateInquiryAction,
    TOpenFooterWizardExpensesDetailAction,
    TOpenLabelEditorAction,
    TOpenMobileCashAction,
    TOpenMobileChangeroleAction,
    TOpenMobileChangeServiceAction,
    TOpenMobileChangeTariffAction,
    TResetBasketAction,
    TResetBasketCountAction,
    TResetBasketSuccess,
    TSendFieldSuggestAction,
    TSendFieldSuggestFailedAction,
    TSendFieldSuggestSuccessfulAction,
    TSendTableFieldsAction,
    TSendTableFieldsSizeAction,
    TSendTableFilterAndSortErrorAction,
    TSendTableFilterAndSortSuccessfulAction,
    TSendTableSearchAction,
    TSendTableSearchErrorAction,
    TSendTableSearchSuccessfulAction,
    TSetBasketContext,
    TSetBasketValidAction,
    TSetIsFastActionPage,
    TSetIsFastBasketPage,
    TSetRangeMonthFilterAction,
    TSetRangeMonthFilterSuccess,
    TSetTableCurrentNameAction,
    TSetTableFilterAction,
    TSetTableFilterPresetAction,
    TSetTableIsLoadingAction,
    TSetTableMainCheckboxAction,
    TSetTableMainCheckboxErrorAction,
    TSetTableSortAction,
    TSetTableSubRowDataAction,
    TSetTableSubRowMessageAction,
    TStartTickAction,
    TStopTickAction,
    TSwitchCheckboxAction,
    TSwitchCheckboxErrorAction,
    TSwitchCheckboxLoadingAction,
    TSwitchCheckboxSuccessfulAction,
    TTableButtonsSetModeAction,
    TTableButtonsSetModePayload,
    TTableFooterPayload,
    TTableModalOpenRemoveAction,
    TTableRemoveRowAction,
    TTableRemoveRowFailureAction,
    TTableRemoveRowSuccessAction,
    TTableResetAction,
    TTableRowDeleteAction,
    TTableRowRestoreAction,
    TTableRowRestoreAllAction,
    TTableSetBasketParamsAction,
    TUpdateRowsStartAction,
    TUpdateRowsStartFailureAction,
    TUpdateRowsStartSuccessfulAction,
} from '../types/tableTypes';


/*
* -------------------------------------------
*           FAST ACTIONS
* -------------------------------------------
*/

export const setBasketContext = (payload: string | undefined): TSetBasketContext =>
    createAction(SET_BASKET_CONTEXT, payload);

export const fastActionSwitchCheckbox = (payload: ISwitchCheckboxPayload): TFastActionSwitchCheckboxAction =>
    createAction(FAST_ACTION_SWITCH_CHECKBOX, payload);

export const setIsFastActionPageAction = (payload: boolean | undefined): TSetIsFastActionPage =>
    createAction(SET_IS_FAST_ACTION_PAGE, payload);

export const setIsFastBasketPageAction = (payload: boolean | undefined): TSetIsFastBasketPage =>
    createAction(SET_IS_FAST_BASKET_PAGE, payload);

/*
 * -------------------------------------------
 *           SET TABLE CURRENT NAME
 * -------------------------------------------
 */

export const setTableCurrentName = (payload: TableNameType): TSetTableCurrentNameAction =>
    createAction(SET_TABLE_CURRENT_NAME, payload);

/*
 * -------------------------------------------
 *           LOAD TABLE META
 * -------------------------------------------
 */

export const loadTableMeta = (payload: ILoadTableMetaPayload): TLoadTableMetaAction =>
    createAction(LOAD_TABLE_META, payload);

export const loadTableMetaSuccessful = (payload: ILoadTableMetaSuccessfulPayload): TLoadTableMetaSuccessfulAction =>
    createAction(LOAD_TABLE_META_SUCCESSFUL, payload);

export const loadTableMetaError = (payload: ILoadTableMetaErrorPayload): TLoadTableMetaErrorAction =>
    createAction(LOAD_TABLE_META_ERROR, payload);

export const loadTableMetaParametersSuccessful = (
    payload: ITableReducer[TableNameType]['parameters'],
): TLoadTableMetaParametersSuccessfulAction => createAction(LOAD_TABLE_META_PARAMETERS_SUCCESSFUL, payload);
/*
 * -------------------------------------------
 *           LOAD TABLE LIST
 * -------------------------------------------
 */

export const loadTableListAction = (payload?: ILoadTableListPayload): TLoadTableListAction =>
    createAction(LOAD_TABLE_LIST, payload);

export const loadTableListFully = (): TLoadTableListFullyAction => createAction(LOAD_TABLE_LIST_FULLY);

export const loadTableListSuccessful = (payload: ILoadTableListSuccessfulPayload): TLoadTableListSuccessfulAction =>
    createAction(LOAD_TABLE_LIST_SUCCESSFUL, payload);

export const loadTableListError = (payload: ILoadTableListErrorPayload): TLoadTableListErrorAction =>
    createAction(LOAD_TABLE_LIST_ERROR, payload);

/*
 * -------------------------------------------
 *           LOAD TABLE SUB ROWS
 * -------------------------------------------
 */
export const addTableSubRowId = (payload: IAddTableSubRowIdPayload): TAddTableSubRowIdAction =>
    createAction(ADD_TABLE_SUB_ROW_ID, payload);

export const loadTableSubRow = (payload: ILoadTableSubRowPayload): TLoadTableSubRowAction =>
    createAction(LOAD_TABLE_SUB_ROW, payload);

export const loadTableSubRowError = (payload: ILoadTableSubRowErrorPayload): TLoadTableSubRowErrorAction =>
    createAction(LOAD_TABLE_SUB_ROW_ERROR, payload);

export const setTableSubRowData = (payload: ISetTableSubRowDataPayload): TSetTableSubRowDataAction =>
    createAction(SET_TABLE_SUB_ROW_DATA, payload);

export const setTableSubRowMessage = (payload: ISetTableSubRowMessagePayload): TSetTableSubRowMessageAction =>
    createAction(SET_TABLE_SUB_ROW_MESSAGE, payload);

export const collapseTableSubRows = (payload: boolean): TCollapseTableSubRows =>
    createAction(COLLAPSE_TABLE_SUB_ROWS, payload);

/*
 * -------------------------------------------
 *           LOAD TABLE FIELDS
 * -------------------------------------------
 */

export const loadTableFieldsSuccessful = (
    payload: ILoadTableFieldsSuccessfulPayload,
): TLoadTableFieldsSuccessfulAction => createAction(LOAD_TABLE_FIELDS_SUCCESSFUL, payload);

export const loadTableFieldsError = (payload: ILoadTableFieldsErrorPayload): TLoadTableFieldsErrorAction =>
    createAction(LOAD_TABLE_FIELDS_ERROR, payload);

/*
 * -------------------------------------------
 *          SEND TABLE FIELDS
 * -------------------------------------------
 */

export const sendTableFields = (payload: ISendTableFieldsPayload): TSendTableFieldsAction =>
    createAction(SEND_TABLE_FIELDS, payload);
export const sendTableFieldsSize = (payload: ISendTableFieldsSizePayload): TSendTableFieldsSizeAction =>
    createAction(SEND_TABLE_FIELDS_SIZE, payload);

/*
 * -------------------------------------------
 *          SET TABLE FILTER & SORT
 * -------------------------------------------
 */

export const setTableFilter = (payload: ISetTableFilterPayload): TSetTableFilterAction =>
    createAction(SET_TABLE_FILTER, payload);

export const setTableSortAction = (payload: ISetTableSortPayload): TSetTableSortAction =>
    createAction(SET_TABLE_SORT, payload);

export const setTableFilterPresetAction = (payload: ISetTableFilterPresetPayload): TSetTableFilterPresetAction =>
    createAction(SET_TABLE_FILTER_PRESET, payload);

/*
 * -------------------------------------------
 *          SET RANGE MONTH FILTER
 * -------------------------------------------
 */

export const setRangeMonthFilter = (payload: ISetRangeMonthFilterPayload): TSetRangeMonthFilterAction =>
    createAction(SET_RANGE_MONTH_FILTER, payload);

export const setRangeMonthFilterSuccess = (): TSetRangeMonthFilterSuccess =>
    createAction(SET_RANGE_MONTH_FILTER_SUCCESS);

/*
 * -------------------------------------------
 *          SEND TABLE FILTER & SORT
 * -------------------------------------------
 */

export const sendTableFilterAndSortSuccess = (): TSendTableFilterAndSortSuccessfulAction =>
    createAction(SEND_TABLE_FILTER_AND_SORT_SUCCESSFUL);

export const sendTableFilterAndSortError = (
    payload: ISendTableFilterAndSortErrorPayload,
): TSendTableFilterAndSortErrorAction => createAction(SEND_TABLE_FILTER_AND_SORT_ERROR, payload);

/*
 * -------------------------------------------
 *                CHECKBOX
 * -------------------------------------------
 */

export const switchCheckbox = (payload: ISwitchCheckboxPayload): TSwitchCheckboxAction =>
    createAction(SWITCH_CHECKBOX, payload);
export const switchCheckboxLoading = (): TSwitchCheckboxLoadingAction => createAction(SWITCH_CHECKBOX_LOADING);
export const switchCheckboxSuccessful = (): TSwitchCheckboxSuccessfulAction => createAction(SWITCH_CHECKBOX_SUCCESSFUL);

export const setTableMainCheckbox = (payload: ISetTableMainCheckboxPayload): TSetTableMainCheckboxAction =>
    createAction(SET_TABLE_MAIN_CHECKBOX, payload);

export const getBasketCountSuccessful = (payload: number): TGetBasketCountSuccessfulAction =>
    createAction(GET_BASKET_COUNT_SUCCESSFUL, payload);

export const getBasketCountFailed = (payload: string): TGetBasketCountFailedAction =>
    createAction(GET_BASKET_COUNT_FAILED, payload);

export const setBasketValid = (payload: boolean): TSetBasketValidAction => createAction(SET_BASKET_VALID, payload);

export const setTableBasketParamsAction = (payload: ITableBasketParams): TTableSetBasketParamsAction =>
    createAction(SET_BASKET_PARAMS, payload);

/*
 * -------------------------------------------
 *            DROP FILTER & SORT
 * -------------------------------------------
 */

export const dropTableFilterAndSort = (payload: IDropTableFilterAndSortPayload): TDropTableFilterAndSortAction =>
    createAction(DROP_TABLE_FILTER_AND_SORT, payload);

/*
 * -------------------------------------------
 *                SEARCH FILTER
 * -------------------------------------------
 */

export const sendTableSearch = (payload: ISendTableSearchPayload): TSendTableSearchAction =>
    createAction(SEND_TABLE_SEARCH, payload);

export const sendTableSearchSuccessful = (
    payload: ISendTableSearchSuccessfulPayload,
): TSendTableSearchSuccessfulAction => createAction(SEND_TABLE_SEARCH_SUCCESSFUL, payload);

export const sendTableSearchError = (payload: ISendTableSearchErrorPayload): TSendTableSearchErrorAction =>
    createAction(SEND_TABLE_SEARCH_ERROR, payload);

/*
 * -------------------------------------------
 *                SEND SUGGEST
 * -------------------------------------------
 */
export const sendFieldSuggest = (payload: ISendFieldSuggestPayload): TSendFieldSuggestAction =>
    createAction(SEND_FIELD_SUGGEST, payload);

export const sendFieldSuggestSuccessful = (
    payload: ITableReducer[TableNameType]['filterOptions'],
): TSendFieldSuggestSuccessfulAction => createAction(SEND_FIELD_SUGGEST_SUCCESSFUL, payload);

export const sendFieldSuggestFailed = (payload: string): TSendFieldSuggestFailedAction =>
    createAction(SEND_FIELD_SUGGEST_FAILED, payload);

/*
 * -------------------------------------------
 *            FOOTER MODAL
 * -------------------------------------------
 */
export const changeTableModalValue = (payload: IChangeTableModalValuePayload): TChangeTableModalValueAction =>
    createAction(CHANGE_TABLE_MODAL_VALUE, payload);

export const openBasket = (payload: IOpenBasketActionPayload): TOpenBasketAction => createAction(OPEN_BASKET, payload);

export const openCreateInquiry = (payload: IOpenCreateInquiryPayload): TOpenCreateInquiryAction =>
    createAction(OPEN_CREATE_INQUIRY, payload);

export const openLabelEditor = (payload: TLabelEditorUpdatePayload): TOpenLabelEditorAction =>
    createAction(OPEN_LABEL_EDITOR, payload);

export const openMobileChangerole = (): TOpenMobileChangeroleAction => createAction(OPEN_MOBILE_CHANGROLE);

export const openMobileChangeTariff = (payload: TTableFooterPayload): TOpenMobileChangeTariffAction =>
    createAction(OPEN_MOBILE_CHANGE_TARIFF, payload);

export const openMobileChangeService = (payload: TTableFooterPayload): TOpenMobileChangeServiceAction =>
    createAction(OPEN_MOBILE_CHANGE_SERVICE, payload);

export const openFooterWizardExpensesDetailAction = (): TOpenFooterWizardExpensesDetailAction =>
    createAction(OPEN_FOOTER_WIZARD_EXPENSES_DETAIL);

export const openMobileCash = (payload: IOpenMobileCashPayload): TOpenMobileCashAction =>
    createAction(OPEN_MOBILE_CASH, payload);

export const loadMobileCash = (payload: ILoadMobileCashPayload): TLoadMobileCashAction =>
    createAction(LOAD_MOBILE_CASH, payload);

export const mobileFileCashLoading = (payload: boolean): TMobileCashFileLoading =>
    createAction(MOBILE_CASH_FILE_LOADING, payload);
export const mobileFileCashSuccessful = (payload: boolean): TMobileCashFileLoadingSuccesful =>
    createAction(MOBILE_CASH_FILE_SUCCESSFULL, payload);

export const updateRowsStart = (payload: IUpdateRowsStartPayload): TUpdateRowsStartAction =>
    createAction(UPDATE_ROWS_START, payload);
export const updateRowsStartSuccessful = (): TUpdateRowsStartSuccessfulAction =>
    createAction(UPDATE_ROWS_START_SUCCESSFUL);
export const updateRowsStartFailure = (payload: string): TUpdateRowsStartFailureAction =>
    createAction(UPDATE_ROWS_START_FAILURE, payload);

/*
 * -------------------------------------------
 *            FOOTER TICK
 * -------------------------------------------
 */

export const startTick = (): TStartTickAction => createAction(START_TICK);

export const stopTick = (): TStopTickAction => createAction(STOP_TICK);

/*
 * -------------------------------------------
 *            PROXY
 * -------------------------------------------
 */

export const resetBasketAction = (payload: IResetBasketActionPayload): TResetBasketAction =>
    createAction(RESET_BASKET, payload);

export const resetBasketSuccess = (): TResetBasketSuccess => createAction(RESET_BASKET_SUCCESS);

/*
 * -------------------------------------------
 *            RESET BASKET COUNT
 * -------------------------------------------
 */

export const resetBasketCount = (payload?: IResetBasketCountActionPayload): TResetBasketCountAction =>
    createAction(RESET_BASKET_COUNT, payload);

/*
 * -------------------------------------------
 *            SET TABLE IS LOADING
 * -------------------------------------------
 */

export const setTableIsLoading = (payload: ISetTableIsLoadingPayload): TSetTableIsLoadingAction =>
    createAction(SET_TABLE_IS_LOADING, payload);

/*
 * -------------------------------------------
 *            REMOVE ROW
 * -------------------------------------------
 */

export const tableRemoveRowAction = (payload: ITableRemoveRowActionPayload): TTableRemoveRowAction =>
    createAction(TABLE_REMOVE_ROW_ACTION, payload);
export const tableRemoveRowSuccess = (payload: ITableRemoveRowSuccessPayload): TTableRemoveRowSuccessAction =>
    createAction(TABLE_REMOVE_ROW_SUCCESS, payload);
export const tableRemoveRowFailure = (payload: ITableRemoveRowFailurePayload): TTableRemoveRowFailureAction =>
    createAction(TABLE_REMOVE_ROW_FAILURE, payload);
export const tableModalOpenRemove = (payload: ITableModalOpenRemoveActionPayload): TTableModalOpenRemoveAction =>
    createAction(TABLE_MODAL_OPEN_REMOVE, payload);

/*
 * -------------------------------------------
 *            TABLE RESET
 * -------------------------------------------
 */

export const tableResetAction = (payload: ITableResetActionPayload): TTableResetAction =>
    createAction(TABLE_RESET, payload);

/*
 * -------------------------------------------
 *        BUTTONS MODE
 * -------------------------------------------
 */

export const tableButtonsSetModeAction = (payload: TTableButtonsSetModePayload): TTableButtonsSetModeAction =>
    createAction(TABLE_BUTTONS_SET_MODE, payload);

/*
 * -------------------------------------------
 *        DELETE ROW IN TABLE
 * -------------------------------------------
 */

export const tableRowDeleteAction = (payload: ITableRowActionPayload): TTableRowDeleteAction =>
    createAction(TABLE_ROW_DELETE_ACTION, payload);

export const tableRowRestoreAction = (payload: ITableRowActionPayload): TTableRowRestoreAction =>
    createAction(TABLE_ROW_RESTORE_ACTION, payload);

export const tableRowRestoreAllAction = (payload: ITableTableNamePayload): TTableRowRestoreAllAction =>
    createAction(TABLE_ROW_RESTORE_ALL_ACTION, payload);

export type TableActionsType =
    | TSetIsFastActionPage
    | TSetIsFastBasketPage
    | TSetBasketContext
    | TMobileCashFileLoading
    | TMobileCashFileLoadingSuccesful
    | ILogoutStartSuccessful
    | TAddTableSubRowIdAction
    | TChangeTableModalValueAction
    | TCollapseTableSubRows
    | TDropTableFilterAndSortAction
    | TGetBasketCountAction
    | TGetBasketCountFailedAction
    | TGetBasketCountSuccessfulAction
    | TLoadTableFieldsAction
    | TLoadTableFieldsErrorAction
    | TLoadTableFieldsSuccessfulAction
    | TLoadTableFilterAndSortAction
    | TLoadTableFilterAndSortErrorAction
    | TLoadTableFilterAndSortSuccessfulAction
    | TLoadTableListAction
    | TLoadTableListErrorAction
    | TLoadTableListFullyAction
    | TLoadTableListSuccessfulAction
    | TLoadTableMetaAction
    | TLoadTableMetaErrorAction
    | TLoadTableMetaParametersSuccessfulAction
    | TLoadTableMetaSuccessfulAction
    | TLoadTableSubRowAction
    | TLoadTableSubRowErrorAction
    | TResetBasketAction
    | TResetBasketCountAction
    | TSendFieldSuggestAction
    | TSendFieldSuggestFailedAction
    | TSendFieldSuggestSuccessfulAction
    | TSendTableFieldsAction
    | TSendTableFieldsSizeAction
    | TSendTableFilterAndSortErrorAction
    | TSendTableFilterAndSortSuccessfulAction
    | TSendTableSearchAction
    | TSendTableSearchErrorAction
    | TSendTableSearchSuccessfulAction
    | TSetBasketValidAction
    | TSetTableCurrentNameAction
    | TSetTableFilterAction
    | TSetTableFilterPresetAction
    | TSetTableIsLoadingAction
    | TSetTableMainCheckboxAction
    | TSetTableMainCheckboxErrorAction
    | TSetTableSortAction
    | TSetTableSubRowDataAction
    | TStartTickAction
    | TStopTickAction
    | TSwitchCheckboxErrorAction
    | TTableButtonsSetModeAction
    | TTableModalOpenRemoveAction
    | TTableRemoveRowFailureAction
    | TTableRemoveRowSuccessAction
    | TTableResetAction
    | TTableSetBasketParamsAction
    | TUpdateRowsStartAction
    | TUpdateRowsStartFailureAction
    | TUpdateRowsStartSuccessfulAction
    | TTableRowDeleteAction
    | TTableRowRestoreAction
    | TTableRowRestoreAllAction
    | TSwitchCheckboxLoadingAction
    | TSwitchCheckboxSuccessfulAction;
